import {
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  IconButton,
  ScrollablePane,
  Stack,
  Sticky,
  StickyPositionType,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { useCommonStyles } from 'common/styles';
import {
  EntityDeleteInput
} from 'common/types/globalTypes';
import { useRef, useState } from 'react';
import { CardCompanies } from '../cardCompanies';
import {
  CardAccounts,
  CardAccountsVariables,
  CardAccounts_cardAccounts_nodes,
} from './__generated__/CardAccounts';
import {
  CardHolderDelete,
  CardHolderDeleteVariables,
} from './__generated__/CardHolderDelete';
import {
  CardHolders,
  CardHoldersVariables,
  CardHolders_cardHolders_nodes,
} from './__generated__/CardHolders';
import { CardAccountsList } from './CardAccountsList';
import { CardHoldersList, SortColumnType } from './CardHolders';
import { toOrderByVariable } from './CardHolders/utils';
import { Header } from './Header';
import { useStyles } from './index.styles';
import { PurchaseCardsShimmer } from './Shimmer';

import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { EntityType } from 'common/types/utility';
import CARD_ACCOUNTS from './CardAccounts.graphql';
import CARD_HOLDER_DELETE from './CardHolderDelete.graphql';
import CARD_HOLDERS from './CardHolders.graphql';
export type CardAccountType = CardAccounts_cardAccounts_nodes;
export const PurchaseCards = () => {
  const heading = 'Purchase Cards';
  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [selectedGroup, setSelectedGroup] = useState<CardAccountType | null>();
  const [selectedRows, setSelectedRows] = useState<
    CardHolders_cardHolders_nodes[]
  >([]);
  const {
    data: cardAccountsData,
    loading: cardAccountsLoading,
    refetch,
  } = useQuery<CardAccounts, CardAccountsVariables>(CARD_ACCOUNTS, {
    variables: {
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });
  const [
    getCardHolders,
    {
      data: cardHoldersData,
      loading: cardHoldersLoading,
      variables: cardHoldersVariables,
      networkStatus,
    },
  ] = useLazyQuery<CardHolders, CardHoldersVariables>(CARD_HOLDERS, {
    variables: {
      orderBy: ['CARD_NUMBER_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const [deleteCardHolder] = useMutation<
    CardHolderDelete,
    CardHolderDeleteVariables
  >(CARD_HOLDER_DELETE, { errorPolicy: 'all' });

  const _headerOnClick = (cardAccount: CardAccountType) => {
    if (selectedGroup !== cardAccount) {
      getCardHolders({
        variables: {
          ...cardHoldersVariables,
          filter: {
            cardAccountId: { equalTo: cardAccount.id },
          },
        },
      });
    }
    setSelectedGroup((prevState) => {
      return prevState === cardAccount ? null : cardAccount;
    });
  };
  const refetching =
    cardAccountsLoading && networkStatus !== NetworkStatus.fetchMore;
  const cardAccountIdRef = useRef('');
  const updateCardAccountIdRef = (newItems: string) => {
    cardAccountIdRef.current = newItems;
  };
  const sortColumn = (columnData: SortColumnType) => {
    getCardHolders({
      variables: {
        ...cardHoldersVariables,
        filter: {
          cardAccountId: {
            equalTo: cardAccountIdRef.current,
          },
        },
        orderBy: toOrderByVariable({
          column: columnData.column,
          direction: columnData.direction,
        }),
      },
    });
  };

  const onConfirmDelete = async () => {
    const deletableData: EntityDeleteInput[] = selectedRows
      .filter((ele) => ele?._isDeletable)
      .map((obj) => {
        return { id: obj.id, rowTimestamp: obj._rowTimestamp! };
      });
    const { errors } = await deleteCardHolder({
      variables: { input: { entityDelete: deletableData } },
      update: (cache, { data }) => {
        if (data?.cardHolderDelete?.deletedEntities?.length) {
          const identity = cache.identify({
            ...cardHoldersData?.cardHolders,
          });
          cache.evict({ id: identity });
          cache.gc();
          cache.modify({
            fields: {
              cardHolders(existing, { readField }) {
                if (!existing) return;

                const existingNodes = readField<any[]>('nodes', existing) ?? [];
                const totalCount =
                  readField<number>('totalCount', existing) ?? 0;

                const filteredNodes = existingNodes.filter((existingRecord) => {
                  const existingId = readField<string>('id', existingRecord);
                  return !data.cardHolderDelete?.deletedEntities?.some(
                    (deletableRecord) => existingId === deletableRecord?.id
                  );
                });

                const deletedCount =
                  data?.cardHolderDelete?.deletedEntities?.length || 0;

                return {
                  ...existing,
                  nodes: filteredNodes,
                  totalCount: totalCount - deletedCount,
                };
              },
            },
          });
        }
      },
    });
    if (errors?.length)
      showCustomToast({
        content: `${errors[0].message}`,
        appearance: 'error',
      });
    else {
      setSelectedRows([]);
      showCustomToast({
        content: 'Record deleted Successfully',
        appearance: 'success',
      });
    }
  };

  return (
    <>
      <Stack grow className={styles.container}>
        <ScrollablePane>
          <Stack className={styles.innerContainer}>
            <Sticky stickyPosition={StickyPositionType.Header}>
              <Stack
                tokens={{ childrenGap: 20 }}
                className={commonStyles.listHeaderContainer}
              >
                <Stack
                  horizontal
                  horizontalAlign="space-between"
                  verticalAlign="center"
                  className={commonStyles.listTitleContainer}
                >
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 7 }}
                  >
                    <Text variant="xLarge">{heading}</Text>

                    <HelpIcon appLinkType="PCARD_ACCOUNT" />
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {selectedRows.length > 0 && (
                      <ActionMessageModal
                        entityType={EntityType.CardHolder}
                        disabled={
                          !selectedRows.some(
                            (selected) => selected?._isDeletable
                          )
                        }
                        visible={selectedRows.length > 0}
                        multiple={{
                          validCount: selectedRows.filter(
                            (selected) => selected?._isDeletable
                          ).length,
                          invalidNames: selectedRows
                            .filter((selected) => !selected?._isDeletable)
                            .map(
                              (cannotDelete) => cannotDelete?.cardFullName || ''
                            ),
                        }}
                        onConfirm={onConfirmDelete}
                      />
                    )}
                    <TooltipHost content={'Refresh'}>
                      <IconButton
                        onClick={() => refetch()}
                        iconProps={{ iconName: 'refresh' }}
                      />
                    </TooltipHost>
                    <CardCompanies />
                  </Stack>
                </Stack>
              </Stack>
              <Header />
            </Sticky>
            {cardAccountsLoading ? (
              <PurchaseCardsShimmer />
            ) : (
              <>
                {cardAccountsData?.cardAccounts?.nodes.map(
                  (cardAccount, index) => {
                    const isCardOpen = selectedGroup?.id === cardAccount.id;

                    return (
                      <Stack key={index.toString()}>
                        <CardAccountsList
                          isOpen={isCardOpen}
                          cardAccount={cardAccount}
                          availableCount={cardAccount.cardHolders.totalCount}
                          onHeaderClick={() => {
                            _headerOnClick(cardAccount);
                            updateCardAccountIdRef(cardAccount.id);
                          }}
                          refetching={refetching}
                        />
                        {isCardOpen && (
                          <CardHoldersList
                            cardAccount={cardAccount}
                            data={cardHoldersData?.cardHolders?.nodes || []}
                            cardHoldersLoading={cardHoldersLoading}
                            getSortColumn={sortColumn}
                            onSelectionChanged={setSelectedRows}
                          />
                        )}
                      </Stack>
                    );
                  }
                )}
              </>
            )}
          </Stack>
        </ScrollablePane>
      </Stack>
    </>
  );
};
